import { useEffect, useRef } from 'react';
import './index.less';

const handleJobAdApplyButtonClick = (container, onLayoutChange) => () => {
    const applicationForm = container.querySelector('.application-form');
    if (applicationForm) {
        applicationForm.style.display = 'block';
        applicationForm.style.opacity = 0;
        let opacity = 0;
        const duration = 500;
        const interval = 16;
        const step = interval / duration;
        function fade() {
            opacity += step;
            if (opacity >= 1) {
                applicationForm.style.opacity = 1;
                onLayoutChange?.();
            } else {
                applicationForm.style.opacity = opacity;
                requestAnimationFrame(fade);
            }
        }
        requestAnimationFrame(fade);
        onLayoutChange?.();
    }

    const formRect = applicationForm?.getBoundingClientRect();
    const scrollContainer = container.closest('.form-preview-container') || container;
    const offset = 60;
    if (scrollContainer && applicationForm) {
        scrollContainer.scrollTo({
            top: applicationForm.offsetTop - offset,
            behavior: 'smooth',
        });
    } else if (formRect) {
        const absoluteElementTop = window.pageYOffset + formRect.top;
        window.scrollTo({
            top: absoluteElementTop - offset,
            behavior: 'smooth',
        });
    }
};

const destroyAlpacaPreviewForm = (container) => {
    if (!container || !window.$) return;
    const formRenderEl = container.querySelector('#form_render');
    if (!formRenderEl) return;
    try {
        const $formRenderEl = window.$(formRenderEl);
        if ($formRenderEl.alpaca('exists')) {
            $formRenderEl.alpaca('destroy');
        }
    } catch (e) {
        // Alpaca throws if the element was already removed or never initialized.
    }
};

// Port of the simple template header background setup for the React preview. The inline
// positionBackground() in templates/job_ad_simple.html never runs here (innerHTML does not
// execute <script>). Legacy uses #job-ad-preview (job ad + application form) as the bounds;
// we use the preview container the same way. Keep in sync with the template's inline script.
const setupSimpleHeaderBackground = (container) => {
    const bg = container.querySelector('.job-ad-simple-header-image');
    if (!bg) return;

    const positionBackground = () => {
        if (!container.isConnected || !bg.isConnected) return;
        bg.style.maxHeight = `${container.offsetHeight}px`;
        bg.style.top = '0';
        bg.style.bottom = 'auto';
    };

    positionBackground();
    window.addEventListener('resize', positionBackground);
    const resizeObserver = new ResizeObserver(positionBackground);
    resizeObserver.observe(container);
    const applicationForm = container.querySelector('.application-form');
    if (applicationForm) {
        resizeObserver.observe(applicationForm);
    }

    return {
        positionBackground,
        cleanup: () => {
            window.removeEventListener('resize', positionBackground);
            resizeObserver.disconnect();
        },
    };
};

const JobAdPreviewContainer = ({ html, applyAttributes = {} }) => {
    const containerRef = useRef(null);
    const alpacaFormInstanceRef = useRef(null);
    const renderedHtmlRef = useRef(null);
    const positionBackgroundRef = useRef(null);
    // applyAttributes travels with `html` in the same API response; read it via ref so the
    // html-keyed effect always sees the current values without re-running on identity changes.
    const applyAttributesRef = useRef(applyAttributes);
    applyAttributesRef.current = applyAttributes;

    // Set innerHTML imperatively — React 19 can re-apply dangerouslySetInnerHTML
    // on re-renders even when __html hasn't changed, destroying Alpaca form state.
    useEffect(() => {
        if (!html || !containerRef.current) return;

        // Only set innerHTML when html actually changes
        if (html === renderedHtmlRef.current) return;
        renderedHtmlRef.current = html;

        const container = containerRef.current;
        destroyAlpacaPreviewForm(container);
        container.innerHTML = html;

        const applyAttrs = applyAttributesRef.current || {};
        // The job ad's preview language (from the API), not the recruiter UI locale.
        const previewLang = applyAttrs.lang || 'en';

        const formExampleEl = container.querySelector('#form_example');
        const formRenderEl = container.querySelector('#form_render');
        if (!formExampleEl || !formRenderEl) return;

        let jsonForm;
        try {
            jsonForm = JSON.parse(formExampleEl.textContent.trim());
        } catch (err) {
            console.error('Error parsing form JSON for preview:', err);
            return;
        }

        const applicationForm = new window.alpacaForm();
        alpacaFormInstanceRef.current = applicationForm;
        applicationForm.setLanguage(previewLang);
        applicationForm.postRender = function () {
            return function (control) {
                applicationForm.defaultPostRender(control);
                // Use the job ad's preview-language string (from the API), not the recruiter UI locale.
                const allowFiletype = applyAttrs.allow_filetype || '';
                const infoSpan = document.createElement('span');
                infoSpan.className = 'info-button-icon--new icon-svg-help-circle';
                infoSpan.innerHTML = `<span class='info-box--new'>${allowFiletype}</span>`;
                const attachmentHeader = container.querySelector('.attachment_header_field_class h2');
                if (attachmentHeader && attachmentHeader.nextSibling) {
                    attachmentHeader.parentNode.insertBefore(infoSpan, attachmentHeader.nextSibling);
                } else if (attachmentHeader) {
                    attachmentHeader.parentNode.appendChild(infoSpan);
                }
            };
        };
        applicationForm.init(null, window.$(formRenderEl));
        applicationForm.updateJsonConfiguration(jsonForm);

        if (window.iso_in_local) {
            const { duedate, startdate } = applyAttrs;
            const jobTimezone = applyAttrs.job_timezone || undefined;

            if (duedate) {
                const duedateEl = container.querySelector('#duedate');
                if (duedateEl) {
                    duedateEl.innerHTML = window.iso_in_local(duedate, 'LLL Z', jobTimezone, previewLang, '<span class="timezone">($)</span>');
                }
            }
            if (startdate) {
                const startdateEl = container.querySelector('#startdate');
                if (startdateEl) {
                    startdateEl.innerHTML = window.iso_in_local(startdate, 'LL', jobTimezone, previewLang);
                }
            }
        }

        setTimeout(() => {
            document.getElementById('lottie-player-camera-animation')?.remove();
        }, 1000);
        const onApplyClick = handleJobAdApplyButtonClick(
            container,
            () => positionBackgroundRef.current?.()
        );
        const jobApplyButton = container.querySelectorAll('.job-ad-apply-button');
        jobApplyButton.forEach(button => {
            button.addEventListener('click', onApplyClick);
        });

        return () => {
            try {
                destroyAlpacaPreviewForm(container);
            } catch (e) {
                // ignore cleanup errors
            }
            alpacaFormInstanceRef.current = null;
            jobApplyButton.forEach(button => {
                button.removeEventListener('click', onApplyClick);
            });
        };
    }, [html]);

    // Runs after the effect above has injected the HTML; sizes the simple template header background.
    useEffect(() => {
        if (!html || !containerRef.current) return;
        const setup = setupSimpleHeaderBackground(containerRef.current);
        if (!setup) {
            positionBackgroundRef.current = null;
            return;
        }
        positionBackgroundRef.current = setup.positionBackground;
        return setup.cleanup;
    }, [html]);

    if (!html) {
        return null;
    }

    return <div ref={containerRef} className='form-preview-container' />;
};

export default JobAdPreviewContainer;
